import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";
import { fetchAgents } from "@/lib/api";
import AgentIcon from "@/components/AgentIcon";

export default function AgentHub() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAgents().then((d) => { setAgents(d); setLoading(false); });
  }, []);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 lg:py-16" data-testid="agent-hub-page">
      <div className="mb-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
        <div className="md:col-span-8">
          <div className="ml-eyebrow mb-2">Agent Hub</div>
          <h1 className="text-3xl lg:text-5xl font-semibold tracking-tight">
            Six senior specialists.
            <br />
            <span className="text-[var(--ml-text-secondary)]">Pick the one that fits the problem.</span>
          </h1>
        </div>
        <div className="md:col-span-4 md:text-right ml-mono text-xs uppercase tracking-widest text-[var(--ml-text-secondary)]">
          Powered by Claude Sonnet 4.5
        </div>
      </div>

      {loading ? (
        <SkeletonGrid />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {agents.map((a, idx) => (
            <AgentCard key={a.id} agent={a} index={idx} />
          ))}
        </div>
      )}
    </div>
  );
}

function AgentCard({ agent, index }) {
  return (
    <Link
      to={`/chat/${agent.id}`}
      className="ml-card ml-card-interactive p-6 flex flex-col gap-4 ml-anim-in"
      style={{ animationDelay: `${index * 60}ms` }}
      data-testid={`agent-card-${agent.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div
          className="w-12 h-12 flex items-center justify-center"
          style={{ background: agent.accent, color: "#fff" }}
        >
          <AgentIcon name={agent.icon} size={26} weight="bold" color="#fff" />
        </div>
        <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
          Agent · 0{index + 1}
        </div>
      </div>

      <div>
        <div className="text-lg font-semibold tracking-tight">{agent.name}</div>
        <div className="text-sm text-[var(--ml-text-secondary)] mt-0.5">{agent.role}</div>
      </div>

      <p className="text-[14.5px] text-[var(--ml-text-primary)] leading-relaxed">
        {agent.description}
      </p>

      <div className="flex flex-wrap gap-1.5 mt-1">
        {agent.specialties.map((s) => (
          <span
            key={s}
            className="ml-mono text-[10px] uppercase tracking-wider px-2 py-1 bg-[var(--ml-surface)] border border-[var(--ml-border)] text-[var(--ml-text-secondary)]"
          >
            {s}
          </span>
        ))}
      </div>

      <div className="mt-auto pt-3 border-t border-[var(--ml-border)] flex items-center justify-between">
        <span className="text-sm font-medium text-[var(--ml-primary)]">Start a session</span>
        <ArrowRight size={18} weight="bold" color="#0f62fe" />
      </div>
    </Link>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
      {[...Array(6)].map((_, i) => (
        <div key={`skeleton-${i}`} className="ml-card p-6 h-64 animate-pulse">
          <div className="w-12 h-12 bg-[var(--ml-surface)] mb-4" />
          <div className="h-4 w-2/3 bg-[var(--ml-surface)] mb-2" />
          <div className="h-3 w-full bg-[var(--ml-surface)] mb-1" />
          <div className="h-3 w-5/6 bg-[var(--ml-surface)]" />
        </div>
      ))}
    </div>
  );
}
